import { defineStore } from 'pinia'

export const useInvoiceStore = defineStore('invoices', {
  state: () => ({
    invoices: [
      {
        id: 'INV-001',
        userId: '1',
        userName: 'John Smith',
        fixtureId: '22201',
        fixtureName: 'Los Angeles Lakers vs Boston Celtics',
        amount: 49.99,
        status: 'paid',
        paymentMethod: 'credit_card',
        purchaseDate: '2024-12-06 18:58:06',
        league: 'NBA',
        cardLast4: '4242',
        items: [
          {
            description: 'Los Angeles Lakers vs Boston Celtics',
            details: 'Los Angeles Lakers vs Boston Celtics\nNBA',
            quantity: 1,
            price: 49.99
          }
        ]
      },
      // More sample invoices...
    ],
    stats: {
      totalRevenue: 2499.50,
      monthlyRevenue: 899.99,
      totalPurchases: 45,
      monthlyPurchases: 12,
      averageTicketSize: 55.55
    }
  }),

  getters: {
    getInvoicesByUser: (state) => (userId) => {
      return state.invoices.filter(inv => inv.userId === userId)
    },
    getInvoicesByFixture: (state) => (fixtureId) => {
      return state.invoices.filter(inv => inv.fixtureId === fixtureId)
    },
    getPaidInvoices: (state) => {
      return state.invoices.filter(inv => inv.status === 'paid')
    }
  },

  actions: {
    formatDate(date) {
      try {
        // Handle ISO string format from datetime-local input
        const dateObj = new Date(date)
        if (!isNaN(dateObj.getTime())) {
          return dateObj.toLocaleString('de-DE', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          }).replace(',', '')
        }
        return date // Return original if parsing fails
      } catch (e) {
        console.error('Error formatting date:', e)
        return date
      }
    },

    addInvoice(invoice) {
      this.invoices.push({
        id: `INV-${Date.now()}`,
        status: 'paid',
        purchaseDate: new Date().toISOString(),
        ...invoice
      })
    },
    async createInvoice(invoiceData) {
      try {
        const newId = `INV-${String(this.invoices.length + 1).padStart(3, '0')}`
        
        // Format the invoice data
        const formattedInvoice = {
          id: newId,
          userId: '1',
          userName: invoiceData.userName,
          fixtureId: '22201',
          fixtureName: invoiceData.items[0]?.description || '',
          league: invoiceData.items[0]?.details?.split('\n')[1] || '',
          amount: invoiceData.total,
          status: invoiceData.status,
          paymentMethod: 'credit_card',
          cardLast4: invoiceData.cardLast4,
          // Format dates consistently
          purchaseDate: this.formatDate(invoiceData.purchaseDate),
          dueDate: invoiceData.dueDate ? this.formatDate(invoiceData.dueDate) : '',
          items: invoiceData.items.map(item => ({
            description: item.description,
            details: item.details,
            quantity: item.quantity,
            price: item.price
          }))
        }

        // Comment out API call for demo
        /* 
        const response = await fetch('/api/invoices', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(formattedInvoice)
        })

        if (!response.ok) {
          throw new Error('Failed to create invoice')
        }
        */

        // Just add to store for demo
        this.invoices.unshift(formattedInvoice)
        
        // Update stats if paid
        if (formattedInvoice.status === 'paid') {
          this.updateStats(formattedInvoice)
        }
        
        return formattedInvoice
      } catch (error) {
        console.error('Error creating invoice:', error)
        return null
      }
    },
    updateStats(newInvoice) {
      if (newInvoice.status === 'paid') {
        this.stats.totalRevenue += newInvoice.amount
        this.stats.totalPurchases += 1
        
        // Parse date consistently
        const invoiceDate = new Date(newInvoice.purchaseDate.split(' ')[0].split('.').reverse().join('-'))
        const currentDate = new Date()
        
        if (invoiceDate.getMonth() === currentDate.getMonth() && 
            invoiceDate.getFullYear() === currentDate.getFullYear()) {
          this.stats.monthlyRevenue += newInvoice.amount
          this.stats.monthlyPurchases += 1
        }
        
        this.stats.averageTicketSize = this.stats.totalRevenue / this.stats.totalPurchases
      }
    },
    async updateInvoice(invoiceData) {
      try {
        const index = this.invoices.findIndex(inv => inv.id === invoiceData.id)
        if (index === -1) return null

        // Format the invoice data
        const updatedInvoice = {
          ...this.invoices[index], // Keep existing data
          userName: invoiceData.userName,
          fixtureName: invoiceData.items[0]?.description || '',
          league: invoiceData.items[0]?.details?.split('\n')[1] || '',
          amount: invoiceData.total,
          status: invoiceData.status,
          paymentMethod: 'credit_card',
          cardLast4: invoiceData.cardLast4,
          // Format dates consistently
          purchaseDate: this.formatDate(invoiceData.purchaseDate),
          dueDate: invoiceData.dueDate ? this.formatDate(invoiceData.dueDate) : '',
          items: invoiceData.items
        }

        // Update in store
        this.$patch((state) => {
          state.invoices[index] = updatedInvoice
        })
        
        // Update stats if status changed
        const oldStatus = this.invoices[index].status
        if (oldStatus !== updatedInvoice.status) {
          if (updatedInvoice.status === 'paid') {
            this.updateStats(updatedInvoice)
          }
        }
        
        return updatedInvoice
      } catch (error) {
        console.error('Error updating invoice:', error)
        return null
      }
    }
  }
}) 